import React, {useEffect, useState} from 'react';
import {View, Text, FlatList, StyleSheet, Image, SafeAreaView, ActivityIndicator, TouchableOpacity, Dimensions} from 'react-native';

import {useNavigation, useIsFocused} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import {ModernHeader} from '~components/ModernHeader';
import ModernCard from '~components/ModernCard/ModernCard';
import ModernSpinner from '~components/ModernSpinner';
import {logScreenView} from '~Utils/firebaseAnalytics';
import {useGetOrderList} from '~hooks/event/useGetOrderList';
import {OrderDetail} from '~types/api/event';
import moment from 'moment';
import {SCREENS} from '~constants';
import {NavigationProps} from '~types/navigation/navigation.type';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, typography, borderRadius, shadows} from '~constants/design';
import Animated, {FadeInDown, FadeInLeft, FadeInUp} from 'react-native-reanimated';

const {width: screenWidth} = Dimensions.get('window');

const PurchaseHistory = () => {
  const {colors} = useTheme();
  const navigation = useNavigation<NavigationProps>();
  const {t} = useTranslation();
  const isFocused = useIsFocused();
  const {data: orderList, isLoading, refetch} = useGetOrderList();

  useEffect(() => {
    refetch();
  }, [isFocused]);

  useEffect(() => {
    logScreenView('Purchase History', 'PurchaseHistory');
  }, []);

  const getStatusColor = (status: string) => {
    if (status == 'succeeded') {
      return colors.statusGreen;
    } else if (status == 'canceled') {
      return colors.error;
    } else {
      return colors.warning;
    }
  };

  const getEventStatus = (eventDate: string) => {
    const now = moment();
    const eventMoment = moment(eventDate);
    
    if (eventMoment.isAfter(now)) {
      return {
        label: 'Upcoming',
        color: colors.primary,
        backgroundColor: colors.primary + '20',
      };
    } else {
      return {
        label: 'Past Event',
        color: colors.textSecondary,
        backgroundColor: colors.textSecondary + '20',
      };
    }
  };

  const getBookingStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return 'Booking Confirmed';
      case 'canceled':
      case 'cancelled':
        return 'Booking Cancelled';
      case 'pending':
        return 'Payment Pending';
      case 'failed':
        return 'Payment Failed';
      case 'refunded':
        return 'Refunded';
      default:
        return 'Processing';
    }
  };

  const onOrderClick = (order_id: string) => {
    navigation.navigate(SCREENS.PAYMENT_SUCCESS, {
      order_id: order_id,
    });
  };

  const renderEventItem = ({item, index}: {item: OrderDetail; index: number}) => (
    <Animated.View entering={FadeInLeft.delay(index * 50).duration(500)}>
      <TouchableOpacity
        onPress={() => onOrderClick(item.order_id)}
        activeOpacity={0.7}
        style={styles.orderItem}>
        <View style={styles.itemContent}>
          <View style={styles.imageContainer}>
            <Image
              resizeMode="cover"
              source={{uri: item.event.image_url}}
              style={styles.eventImage}
            />
          </View>
          
          <View style={styles.eventDetails}>
            <View style={styles.headerRow}>
              <Text style={[styles.eventTitle, {color: colors.textPrimary}]} numberOfLines={2}>
                {item.event.name}
              </Text>
            </View>
            
            <View style={styles.infoRow}>
              <Text style={[styles.dateText, {color: colors.textSecondary}]}>
                {moment(item.event.start_date).format('DD MMM YYYY • HH:mm')}
              </Text>
              <Text style={[styles.eventStatusText, {color: getEventStatus(item.event.start_date).color}]}>
                {getEventStatus(item.event.start_date).label}
              </Text>
            </View>
            
            <View style={styles.statusRow}>
              <View style={[styles.mainStatusBadge, {backgroundColor: getStatusColor(item.status) + '15'}]}>
                <Text style={[styles.mainStatusText, {color: getStatusColor(item.status)}]}>
                  {getBookingStatusText(item.status)}
                </Text>
              </View>
            </View>
            
            <View style={styles.priceRow}>
              <Text style={[styles.priceText, {color: colors.primary}]}>
                {item.currency} {item.total_amount}
              </Text>
            </View>
          </View>
        </View>
        <View style={[styles.divider, {backgroundColor: colors.border}]} />
      </TouchableOpacity>
    </Animated.View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
    },
    loaderContainer: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.1)',
    },
    // Order Item Styles
    orderItem: {
      backgroundColor: colors.background,
    },
    itemContent: {
      flexDirection: 'row',
      paddingVertical: spacing.xl,
      paddingHorizontal: spacing.lg,
    },
    imageContainer: {
      marginRight: spacing.lg,
      borderRadius: borderRadius.md,
      overflow: 'hidden',
    },
    eventImage: {
      width: 90,
      height: 90,
      borderRadius: borderRadius.lg,
      backgroundColor: '#f5f5f5',
    },
    eventDetails: {
      flex: 1,
    },
    headerRow: {
      marginBottom: spacing.sm,
    },
    eventTitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.fontSize.base * 1.2,
    },
    statusRow: {
      marginBottom: spacing.sm,
    },
    mainStatusBadge: {
      paddingHorizontal: spacing.sm,
      paddingVertical: 4,
      borderRadius: borderRadius.sm,
      alignSelf: 'flex-start',
    },
    divider: {
      height: 1,
      marginHorizontal: spacing.xl,
      opacity: 0.3,
    },
    eventStatusText: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
      textAlign: 'center',
    },
    infoRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    dateText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    priceRow: {
      marginTop: spacing.xs,
    },
    priceText: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
    },
    mainStatusText: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
      textAlign: 'center',
    },

    // Empty State Styles
    emptyStateContainer: {
      flex: 1,
      marginTop: spacing.xxxl,
    },
    emptyStateContent: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: spacing.xxxl,
      paddingHorizontal: spacing.xl,
      borderRadius: borderRadius.lg,
      marginHorizontal: spacing.lg,
    },
    emptyStateIcon: {
      fontSize: 64,
      marginBottom: spacing.lg,
    },
    emptyStateTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      marginBottom: spacing.sm,
      textAlign: 'center',
    },
    emptyStateSubtitle: {
      fontSize: typography.fontSize.base,
      textAlign: 'center',
      lineHeight: typography.fontSize.base * 1.4,
      maxWidth: screenWidth * 0.7,
    },
  });

  return (
    <SafeAreaView style={{flex: 1, backgroundColor: colors.background}}>
      <ModernHeader
        title={t('settings.purchase_history') || 'Purchase History'}
        subtitle={
          orderList?.length
            ? `${orderList.length} ${t('settings.orders_found') || 'orders found'}`
            : t('settings.your_orders') || 'Your orders'
        }
        variant="default"
      />
      <View style={styles.container}>
        <FlatList
          data={orderList}
          keyExtractor={item => item.order_id}
          renderItem={renderEventItem}
          ListEmptyComponent={() => {
            if (isLoading) {
              return null;
            }
            return (
              <Animated.View entering={FadeInUp.duration(600)} style={styles.emptyStateContainer}>
                <View style={[styles.emptyStateContent, {backgroundColor: colors.surface}]}>
                  <Text style={styles.emptyStateIcon}>🛍️</Text>
                  <Text style={[styles.emptyStateTitle, {color: colors.textPrimary}]}>
                    No purchases yet
                  </Text>
                  <Text style={[styles.emptyStateSubtitle, {color: colors.textSecondary}]}>
                    Your purchase history will appear here once you buy tickets for events
                  </Text>
                </View>
              </Animated.View>
            );
          }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            flexGrow: 1,
          }}
        />
      </View>
      {isLoading && (
        <Animated.View entering={FadeInDown.duration(300)} style={styles.loaderContainer}>
          <ModernSpinner size={40} color={colors.primary} />
        </Animated.View>
      )}
    </SafeAreaView>
  );
};

export default PurchaseHistory;
